import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType, Glyphs, MenuItem, TscPopoverOrigin, PopoverButton, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import Constants, {GroupRoleTypes} from '../../constants/Constants';
import {collectionsStore, refreshActiveCollection, removeCollectionFromState, updateActiveCollection, addCollectionToState} from '../../context/collectionsProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {themeStore} from '../../context/themeProvider';
import {ICollectionModel} from '../../model/appModel';
import config from '../../service/config';
import collectionApi from '../../service/collectionApi';
import logging from '../../service/logging';
import LeaveCollectionModal from '../modals/LeaveCollectionModal';
import cssConstants from '../../constants/cssConstants';
import DeleteModal from '../modals/DeleteModal';
import CollectionLeaveGlyph from '../../static/svg/leave-16x16.svg';
import AddYoutubeMediaModal from '../modals/AddYoutubeMediaModal';
import RenameCollectionModal from '../modals/RenameCollectionModal';
import {GridLayoutButtonGroup} from '../util/StyledElements';
import ShareCollectionModal from '../modals/ShareCollectionModal';
import {IMediaDetails} from '../../model/mediaDetails';
import {batchSelectionStore} from '../../context/batchSelectionProvider';
import CollectionMediaRemove from '../details/CollectionMediaRemove';
import CollectionNotificationToggle from './CollectionNotificationToggle';

export const CollectionActionMenuBase: React.FC<ICollectionActionMenuProps & IStateMappedProps> = ({isManagementView, activeCollection, theme, selectedMedia}) => {
   const {t} = useTranslation();

   const [showShareModal, setShowShareModal] = useState(false);
   const [showLeaveModal, setShowLeaveModal] = useState(false);
   const [showDeleteModal, setShowDeleteModal] = useState(false);
   const [showYoutubeModal, setShowYoutubeModal] = useState(false);
   const [showRenameModal, setShowRenameModal] = useState(false);
   const [showBatchRemoveModal, setShowBatchRemoveModal] = useState(false);
   const [activeCollectionRole, setActiveCollectionRole] = useState(activeCollection?.Role);

   useEffect(() => {
      setActiveCollectionRole(activeCollection?.Role ?? GroupRoleTypes.notMember);
   }, [activeCollection]);

   const onJoinCollection = async () => {
      try {
         const joinedCollection = await collectionApi.joinCollection(activeCollection.Id);
         addCollectionToState(joinedCollection);
         await refreshActiveCollection();
      } catch (e) {
         logging.error(`Failed to join collection ${activeCollection.Id} with error: `, e);
      }
   };

   const onLeaveCollection = async () => {
      activeCollection.CanLeave = false;
      activeCollection.Role = GroupRoleTypes.notMember;
      const selfMemberIndex = activeCollection.Members.findIndex(m => m.toLowerCase() === config.user.EmailAddress.toLowerCase());
      if (selfMemberIndex !== -1) {
         activeCollection.Members.splice(selfMemberIndex, 1);
      }
      const selfMemberDetailsIndex = activeCollection.MemberDetails.findIndex(m => m.TechSmithId === config.user.TechSmithId);
      if (selfMemberDetailsIndex !== -1) {
         activeCollection.MemberDetails.splice(selfMemberDetailsIndex, 1);
      }
      await updateActiveCollection(activeCollection);
      removeCollectionFromState(activeCollection, false);
   };

   const onDeleteCollection = () => {
      removeCollectionFromState(activeCollection, true);
   };

   const userCanAdmin = config.user.IsSignedIn && (
      config.isAdmin ||
      activeCollection?.Role === GroupRoleTypes.manager
   );

   const userCanContribute = config.user.IsSignedIn && (
      config.isAdmin ||
      activeCollection?.Role === GroupRoleTypes.manager ||
      activeCollection?.Role === GroupRoleTypes.contributor
   );

   const areItemsSelected = selectedMedia?.length > 0;
   const showLeave = !isManagementView && config.user.IsSignedIn && activeCollection?.CanLeave;
   const showManage = !isManagementView && config.user.IsSignedIn && userCanAdmin;
   const showJoin = !areItemsSelected && config.user.IsSignedIn && activeCollectionRole === GroupRoleTypes.notMember;
   const showLoginToJoin = !isManagementView && !config.user.IsSignedIn;
   const showShare = !areItemsSelected;
   const showNotificationButton = config.featureSwitches.IsScreencast && config.user.IsSignedIn && activeCollectionRole !== GroupRoleTypes.notMember;

   const showMoreDelete = isManagementView && userCanAdmin;
   const showMoreLeave = isManagementView && activeCollection?.CanLeave;
   const showMoreAddYouTube = config.IsYouTubeEnabled && isManagementView && userCanContribute;
   const showMoreRename = isManagementView && userCanAdmin;
   const showMore = isManagementView && !areItemsSelected &&
      (showMoreDelete || showMoreLeave || showMoreAddYouTube || showMoreRename);

   const moreMenu = (
      <>
         {showMoreDelete && (
            <MenuItem
               key="delete"
               label={t('collections.options.delete')}
               glyph={<Glyphs.Delete16x16 />}
               onClick={() => setShowDeleteModal(true)}
               testId="collection-actions-more-delete"
            />
         )}
         {showMoreLeave && (
            <MenuItem
               key="leave"
               label={t('collections.options.leave')}
               glyph={<CollectionLeaveGlyph />}
               onClick={() => setShowLeaveModal(true)}
               testId="collection-actions-more-leave"
            />
         )}
         {showMoreAddYouTube && (
            <MenuItem
               key="addyoutube"
               label={t('collections.options.importYoutube')}
               glyph={<Glyphs.Upload16x16 />}
               onClick={() => setShowYoutubeModal(true)}
               testId="collection-actions-more-addyoutube"
            />
         )}
         {showMoreRename && (
            <MenuItem
               key="rename"
               label={t('collections.options.rename')}
               glyph={<Glyphs.Edit16x16 />}
               onClick={() => setShowRenameModal(true)}
               testId="collection-actions-more-rename"
            />
         )}
      </>
   );

   const collectionMoreMenuButton = (
      <PopoverButton
         style={{}}
         buttonGlyph={<Glyphs.EllipsisStacked16x16 />}
         origin={TscPopoverOrigin.bottomRight}
         themeName={theme}
         showArrow={false}
         buttonText={t('More')}
         buttonTitle={t('More')}
         buttonType={TscButtonType.tertiary}
         mobileContextMenuTitle={t('collections.options.title', {collectionName: activeCollection?.Name})}
         popoverMenu={moreMenu}
         useMobileMenu={true}
         mobileContextMenuZindex={cssConstants.zIndex.mobileContextMenu}
         closeMenuOnClick={true}
         popoverMenuMaxHeight={cssConstants.contextMenuMaxHeight}
         testId="collection-actions-more"
      />
   );

   return (
      <>
         <GridLayoutButtonGroup>
            {showShare && (
               <ControlButton
                  buttonType={TscButtonType.tertiary}
                  themeName={theme}
                  glyph={<Glyphs.Share16x16 />}
                  title={t('share.action')}
                  label={t('share.action')}
                  testId="collection-actions-share"
                  onClick={() => setShowShareModal(true)}
               />
            )}
            {showLeave && (
               <ControlButton
                  buttonType={TscButtonType.secondary}
                  themeName={theme}
                  onClick={() => setShowLeaveModal(true)}
                  title={t('collections.options.leave')}
                  label={t('collections.options.leave')}
                  testId="collection-actions-leave"
               />
            )}
            {showManage && (
               <ControlButton
                  buttonType={TscButtonType.primary}
                  themeName={theme}
                  href={`${Constants.navigation.myCollectionsLink}/${activeCollection?.Id}`}
                  title={t('collections.options.manage')}
                  label={t('collections.options.manage')}
                  testId="collection-actions-manage"
               />
            )}
            {showJoin && (
               <ControlButton
                  buttonType={TscButtonType.secondary}
                  onClick={onJoinCollection}
                  themeName={theme}
                  title={t('collections.options.join')}
                  label={t('collections.options.join')}
                  testId="collection-actions-join"
               />
            )}
            {showLoginToJoin && (
               <ControlButton
                  buttonType={TscButtonType.secondary}
                  href={`/register?redirect=/collections/${activeCollection?.Id}`}
                  themeName={theme}
                  title={t('collections.options.signInToFollow')}
                  label={t('collections.options.signInToFollow')}
                  testId="collection-actions-signInToFollow"
               />
            )}
            {showNotificationButton && <CollectionNotificationToggle />}
            {showMore && collectionMoreMenuButton}
            {areItemsSelected && (
               <ControlButton
                  title={t('removeMedia.removeMedia')}
                  glyph={<Glyphs.CircleRemove16x16 />}
                  label={t('removeMedia.removeButtonLabel')}
                  buttonType={TscButtonType.tertiary}
                  themeName={theme}
                  onClick={() => setShowBatchRemoveModal(true)}
                  testId="collection-actions-batch-remove"
               />
            )}

            {showLeaveModal && (
               <LeaveCollectionModal
                  collection={activeCollection}
                  onClose={() => setShowLeaveModal(false)}
                  onLeave={onLeaveCollection}
               />
            )}
            {showShareModal && <ShareCollectionModal onClose={() => setShowShareModal(false)} />}
            {showDeleteModal && (
               <DeleteModal
                  onClose={() => setShowDeleteModal(false)}
                  onDelete={onDeleteCollection}
                  collectionsToDelete={[activeCollection]}
                  title={t('collections.delete.title')}
                  warning={t('collections.delete.warning', {collectionName: activeCollection?.Name})}
               />
            )}
            {showYoutubeModal && (
               <AddYoutubeMediaModal
                  onClose={() => setShowYoutubeModal(false)}
                  collectionId={activeCollection?.Id}
               />
            )}
            {showRenameModal && <RenameCollectionModal onClose={() => setShowRenameModal(false)} />}
            {showBatchRemoveModal && (
               <CollectionMediaRemove
                  onClose={() => setShowBatchRemoveModal(false)}
                  collection={activeCollection}
                  media={selectedMedia}
               />
            )}
         </GridLayoutButtonGroup>
      </>
   );
};

export interface ICollectionActionMenuProps {
   isManagementView: boolean;
}

export interface IStateMappedProps {
   theme: TscThemeNames;
   activeCollection: ICollectionModel;
   selectedMedia: IMediaDetails[];
}

const mapStatesToProps = (
   {isManagementView}: ICollectionActionMenuProps,
   {theme}: Partial<IStateMappedProps>,
   {activeCollection}: Partial<IStateMappedProps>,
   {selectedMedia}: Partial<IStateMappedProps>
): ICollectionActionMenuProps & IStateMappedProps => ({isManagementView, theme, activeCollection, selectedMedia});

export default withMemoizedContexts(mapStatesToProps, themeStore, collectionsStore, batchSelectionStore)(CollectionActionMenuBase);
